import { Ban, CalendarClock, CircleCheck, CirclePause, CircleX, Clock, FlaskConical, Gauge } from 'lucide-preact'
import { BLAST_STATE_COLORS, BLAST_STATE_LABELS, type EmailBlastState } from '@/types/emailBlast'

type BadgeShown = EmailBlastState | 'Scheduled'

// Each state is a word and an icon, never a colour alone (ORB-953).
const ICONS: Record<BadgeShown, typeof Clock> = {
  Scheduled: CalendarClock,
  Canary: FlaskConical,
  Measuring: Gauge,
  Releasing: Clock,
  Paused: CirclePause,
  Complete: CircleCheck,
  Failed: CircleX,
  Cancelled: Ban,
}

interface BlastStateBadgeProps {
  state: EmailBlastState
  /**
   * What to show instead of the state. Presentational only; there is no such state and none should be
   * added. Scheduled (ORB-706): materialization runs a 24-hour lookahead, so a canary can sit with nothing
   * sent for up to a day, and "Canary" there describes the page's internals. Paused (ORB-953): a broadcast
   * whose sending domain stopped is held like a paused one, whatever its own state.
   */
  presentAs?: 'Scheduled' | 'Paused'
  className?: string
}

/** Lifecycle pill for an email blast. Color signals progress vs. trouble (see BLAST_STATE_COLORS). */
export function BlastStateBadge({ state, presentAs, className = '' }: BlastStateBadgeProps) {
  const shown: BadgeShown = presentAs ?? state
  const Icon = ICONS[shown]
  if (!Icon) {
    console.error('Unknown broadcast state', state)
    return null
  }
  const color =
    shown === 'Scheduled' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300' : BLAST_STATE_COLORS[shown]
  const label = shown === 'Scheduled' ? 'Scheduled' : BLAST_STATE_LABELS[shown]
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${color} ${className}`}>
      <Icon className="w-3 h-3 shrink-0" aria-hidden="true" />
      {label}
    </span>
  )
}
